import { ComponentProps, useState } from 'react';

import { ButtonBase, styled } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';

import { theme as staticTheme } from '../../theme';
import { Box } from '../box/box';
import { StateAwareGridPremium } from '../data-grid-premium/state-aware-grid-premium';
import { useRedesignDialogs } from '../dialog/dialog-message';
import { useTheme } from '../styles/styles';
import { cardSurfaceSx, cardTitleSx } from './card';
import { ColumnsPopover } from './columns-popover';
import { ProfileTabTitleContainer } from './containers';
import { GridEmptyState } from './grid-empty-state';
import {
  cardIconButtonSx,
  SortFilterButton,
  sortFilterHeaderSlots,
} from './sort-filter-button';
import { getSortFilterSupport } from './sort-filter-popover';

export { SortFilterButton } from './sort-filter-button';

export const BasicTableStyled = styled(StateAwareGridPremium)(({ theme }) => ({
  backgroundColor: theme.colors.alpha.white[100],
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'transparent',
  },

  '& .MuiDataGrid-sortIcon': {
    backgroundColor: theme.palette.primary.light,
    width: '16px',
    height: '16px',
    border: `0.5px solid ${theme.border?.sortButton}`,
    borderRadius: theme.borderRadius?.borderRadiusXSmall,
    color: theme.palette.primary.main,
    flex: 1,
  },
}));

// Width of a pinned row-actions column that fits its icon buttons: 30px
// buttons, 4px apart, centred with the cell's 14px padding either side.
export const actionsColumnWidth = (buttons: number) =>
  buttons * 30 + Math.max(buttons - 1, 0) * 4 + 28;

export const EmployeeProfileTableStyled = (
  props: ComponentProps<typeof StateAwareGridPremium> & {
    // Usually text; may carry a muted count after it (e.g. "Import log 12 rows").
    headerTitle?: React.ReactNode;
    headerRightContent?: React.ReactNode;
    // Header controls placed before the columns button (e.g. a filter).
    headerLeadContent?: React.ReactNode;
    // Extra styles for the titled card (e.g. to fill a fixed-height panel).
    cardSx?: Record<string, unknown>;
    // Card look (header row with the columns button) even without a title.
    card?: boolean;
    // Empty state title (default below); filtering to nothing shows "No matches".
    emptyTitle?: string;
    // The "Sort & filter" button sits outside the grid (e.g. a section
    // header); the grid still drops its column menu for it.
    externalSortFilter?: boolean;
    // Class for the card's header row (e.g. a dialog's drag handle).
    headerClassName?: string;
  }
) => {
  const {
    headerTitle,
    headerRightContent,
    headerLeadContent,
    cardSx,
    card,
    emptyTitle,
    externalSortFilter,
    headerClassName,
    ...gridProps
  } = props;
  const theme = useTheme();
  const hasHeader = Boolean(headerTitle || headerRightContent || card);
  const redesign = useRedesignDialogs();
  const emptyStateSlots = {
    noRowsOverlay: () => (
      <GridEmptyState title={emptyTitle ?? 'Nothing here yet'} />
    ),
    noResultsOverlay: () => <GridEmptyState icon="search" title="No matches" />,
  };
  const internalApiRef = useGridApiRef();
  const apiRef = props.apiRef ?? internalApiRef;
  // Cards get a "Sort & filter" button when the grid has anything to sort or
  // filter; it replaces the column menu, its filter panel and header icons.
  const sortFilterFlags = {
    disableColumnMenu: gridProps.disableColumnMenu,
    disableColumnFilter: gridProps.disableColumnFilter,
    disableColumnSorting: gridProps.disableColumnSorting,
  };
  const sortFilter = getSortFilterSupport(gridProps.columns, sortFilterFlags);
  const showSortFilter =
    (hasHeader || externalSortFilter) && (sortFilter.sort || sortFilter.filter);
  const sortFilterButton = showSortFilter && (
    <SortFilterButton apiRef={apiRef} flags={sortFilterFlags} />
  );

  const grid = (
    <StateAwareGridPremium
      columnHeaderHeight={hasHeader ? 38 : 55}
      rowHeight={hasHeader ? 53 : 65}
      density={'standard'}
      // Callers can still pass their own height.
      autoHeight={hasHeader}
      {...gridProps}
      slots={{
        // New empty states only in the Employee Center (redesign flag).
        ...(redesign && emptyStateSlots),
        ...gridProps.slots,
        ...(showSortFilter && sortFilterHeaderSlots),
      }}
      disableColumnMenu={showSortFilter || gridProps.disableColumnMenu}
      apiRef={apiRef}
      // Callers' sx goes last; it may be an object, a function or an array.
      sx={[
        {
          '--DataGrid-rowBorderColor': theme.custom?.tableRowLine,
          // Rows never scroll inside the card; columns do when they don't fit
          // (phones), so keep the thin horizontal scrollbar.
          '& .MuiDataGrid-scrollbar--vertical': {
            display: 'none !important',
          },
          ...(hasHeader ? {} : { height: '55vh' }),
          ...(hasHeader
            ? { border: 'none', borderRadius: 0, boxShadow: 'none' }
            : {
                boxShadow: theme.custom?.cardBoxShadow,
                border: `1px solid ${theme.background.lightGrey3}`,
                borderRadius: '10px',
              }),
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: 'none',
          },
          '&& .MuiDataGrid-columnHeader, && .MuiDataGrid-columnHeaders .MuiDataGrid-filler':
            {
              backgroundColor: theme.background.surface,
              borderBottom: `1px solid ${theme.custom?.tableHeaderLine}`,
            },
          // Room for the empty state when there are no rows.
          ...(redesign && {
            '& .MuiDataGrid-overlayWrapper': { minHeight: 140 },
            // The empty state fills the space under the headers (the grid sizes
            // its overlay once, before the card stretches), so it's centred in
            // the table both ways. Loading overlays keep the grid's own sizing.
            '& .MuiDataGrid-overlayWrapper:has(> .MuiDataGrid-overlayWrapperInner > [role="status"])':
              {
                flex: '1 0 auto',
                display: 'flex',
                flexDirection: 'column',
                '& > .MuiDataGrid-overlayWrapperInner': {
                  flex: 1,
                  height: 'auto !important',
                },
              },
            // With no rows the grid still reserves an empty row area under the
            // overlay; drop it so the empty state isn't pushed to the top.
            '&:has(.MuiDataGrid-overlayWrapperInner > [role="status"]) .MuiDataGrid-virtualScrollerContent':
              {
                flexBasis: '0 !important',
                height: '0 !important',
                minHeight: 0,
              },
            '&:has(.MuiDataGrid-overlayWrapperInner > [role="status"]) .MuiDataGrid-virtualScroller > .MuiDataGrid-filler':
              { display: 'none' },
          }),
          '& .MuiDataGrid-scrollShadow': { display: 'none' },
          '& .MuiDataGrid-columnSeparator': {
            opacity: 0,
            transition: 'opacity 120ms',
          },
          '& .MuiDataGrid-columnHeaders:hover .MuiDataGrid-columnSeparator': {
            opacity: 1,
          },
          '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
            paddingLeft: '14px',
            paddingRight: '14px',
          },
          // Headers carry aria-colindex (1-based), cells data-colindex.
          '& .MuiDataGrid-columnHeader[aria-colindex="1"], & .MuiDataGrid-cell[data-colindex="0"]':
            {
              paddingLeft: '20px',
            },
          '&& .MuiDataGrid-columnHeaderCheckbox, && .MuiDataGrid-cellCheckbox':
            {
              paddingLeft: 0,
              paddingRight: 0,
              justifyContent: 'center',
            },
          '& .MuiDataGrid-virtualScroller': {
            '--DataGrid-t-cell-background-pinned': theme.background.white,
            clipPath: 'inset(1px 0 0 0)',
          },
          '& .MuiDataGrid-filler--pinnedRight': {
            borderLeft: 'none',
          },
          '& .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-columnHeader--pinnedRight':
            {
              borderLeft: 'none',
              boxShadow: `inset 1px 0 0 ${theme.custom?.pinnedColumnLine}`,
            },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '12.5px',
            fontWeight: 500,
            color: theme.custom?.tableHeaderText,
            lineHeight: '17.5px',
            letterSpacing: 'normal',
          },
          '& .MuiDataGrid-cell': {
            color: theme.custom?.brandNavy,
            fontSize: '13.5px',
            fontWeight: 400,
            letterSpacing: 'normal',
          },
          '& .MuiDataGrid-cell[data-colindex="0"]': {
            fontWeight: 500,
          },
          '& .MuiDataGrid-cell[data-field="actions"]': {
            '& > div, & > .MuiBox-root': { gap: '4px' },
            '& .MuiIconButton-root, & .MuiButton-root': {
              width: 30,
              height: 30,
              minWidth: 30,
              padding: 0,
              borderRadius: '8px',
              border: `1px solid ${theme.custom?.controlBorder}`,
              background: theme.background.white,
              color: theme.custom?.brandIndigo,
              boxShadow: 'none',
              '& svg, & .MuiSvgIcon-root': {
                width: 16,
                height: 16,
                color: 'inherit',
              },
              '&:hover': { background: theme.custom?.listButtonHoverBg },
              '&.Mui-disabled': { opacity: 0.45 },
            },
            // The delete icon's path starts with "M5 7h14".
            '& .MuiIconButton-root:has(path[d^="M5 7h14"]):hover, & .MuiButton-root:has(path[d^="M5 7h14"]):hover':
              {
                color: theme.palette.error.main,
                background: theme.custom?.messageDangerBg,
              },
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeaders .MuiDataGrid-pinnedColumnsSectionSeparator':
            {
              display: 'none',
            },
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );

  if (!hasHeader) return grid;

  if (card && !headerTitle && !headerRightContent) {
    return (
      <Box
        sx={{
          position: 'relative',
          ...cardSurfaceSx(theme),
          paddingBottom: '8px',
          overflow: 'hidden',
          '& .MuiDataGrid-columnHeaders': {
            paddingRight: showSortFilter ? '88px' : '48px',
          },
          ...cardSx,
        }}
      >
        {grid}
        {showSortFilter && (
          <SortFilterButton
            apiRef={apiRef}
            flags={sortFilterFlags}
            sx={{ position: 'absolute', top: '3px', right: '48px', zIndex: 2 }}
          />
        )}
        <ColumnsButton
          apiRef={apiRef}
          sx={{
            position: 'absolute',
            top: '3px',
            right: '8px',
            zIndex: 2,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...cardSurfaceSx(theme),
        padding: '6px 0 8px',
        overflow: 'hidden',
        ...cardSx,
      }}
    >
      <Box
        className={headerClassName}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '16px 16px 14px 20px',
          borderBottom: `1px solid ${theme.custom?.tableTitleDivider}`,
          '& .MuiButton-root': {
            height: '34px',
            padding: '0 14px',
            borderRadius: '8px',
            border: `1px solid ${theme.custom?.controlBorder}`,
            background: theme.background.white,
            color: theme.custom?.brandIndigo,
            fontSize: '13.5px',
            fontWeight: 400,
            boxShadow: 'none',
            '&:hover': {
              background: theme.custom?.hoverBg,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              color: theme.text.disabled,
              borderColor: theme.custom?.cardDivider,
            },
            '& .MuiButton-startIcon': {
              marginRight: '6px',
              '& svg': { width: 16, height: 16 },
            },
          },
        }}
      >
        <ProfileTabTitleContainer
          sx={{
            flex: 1,
            minWidth: 0,
            ...cardTitleSx(theme),
          }}
        >
          {headerTitle}
        </ProfileTabTitleContainer>
        {headerLeadContent}
        {sortFilterButton}
        <ColumnsButton apiRef={apiRef} />
        {headerRightContent}
      </Box>
      {grid}
    </Box>
  );
};

export const ColumnsButton = ({
  apiRef,
  sx,
}: {
  apiRef: ReturnType<typeof useGridApiRef>;
  sx?: Record<string, unknown>;
}) => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <ButtonBase
        aria-label="Show / hide columns"
        title="Show / hide columns"
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ ...cardIconButtonSx(theme), ...sx }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          aria-hidden
          sx={{
            width: 16,
            height: 16,
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.8,
            strokeLinejoin: 'round',
          }}
        >
          <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
          <path d="M9.5 4.5v15M14.5 4.5v15" />
        </Box>
      </ButtonBase>
      <ColumnsPopover
        apiRef={apiRef}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
      />
    </>
  );
};

// Titled tables now size to their rows, so this no longer caps the height.
export const EmployeeProfileTableHalfPageStyled = EmployeeProfileTableStyled;

export const PinnedRightDesign = (width: number = 60) => ({
  '& .MuiDataGrid-cell--pinnedRight': {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& .MuiDataGrid-pinnedColumns--right': {
    width: `${width}px !important`,
    minWidth: `${width}px !important`,
    maxWidth: `${width}px !important`,
    '--DataGrid-width-rightPinnedColumns': `${width}px`,
  },
  '& .MuiDataGrid-columnHeader--pinnedRight': {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  },
  '& .MuiDataGrid-filler--pinnedRight': {
    borderLeft: 0,
  },

  '& .MuiDataGrid-virtualScroller': {
    '--DataGrid-t-cell-background-pinned': staticTheme.background.white,
  },
});
